/**
 * useCurrencyHandlers Hook
 * 
 * Handles all currency-related operations (add, edit, delete, refresh, set default).
 */

import React from "react";
import { auth, db } from "../../../firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  getDocs,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { Currency } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { DemoModeMessage } from "../../../utils/demoModeMessage";
import { COMMON_CURRENCIES } from "./useDefaultCurrency";
import { getDefaultDecimalPlaces } from "../../../utils/currencyDefaults";

export interface UseCurrencyHandlersParams {
  workspaceId: string;
  defaultCurrency: string;
  currencies: Currency[];
  setDefaultCurrency: React.Dispatch<React.SetStateAction<string | null>>;
  setCurrencies: React.Dispatch<React.SetStateAction<Currency[]>>;
  fetchExchangeRatesFromAPI: (baseCurrency: string, targetCurrencies: string[]) => Promise<Record<string, number | null>>;
  formatRate: (rate: number | string) => string;
  
  // UI state setters
  setAddDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setNewCurrencyCode: React.Dispatch<React.SetStateAction<{ code: string; name: string; symbol: string } | null>>;
  setAddingCurrency: React.Dispatch<React.SetStateAction<boolean>>;
  setEditDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingCurrency: React.Dispatch<React.SetStateAction<Currency | null>>;
  setEditCurrencyRate: React.Dispatch<React.SetStateAction<string>>;
  setDeleteDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrencyToDelete: React.Dispatch<React.SetStateAction<string>>;
  setDefaultCurrencyDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedDefaultCurrency: React.Dispatch<React.SetStateAction<string>>;
  setRefreshDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrencyToRefresh: React.Dispatch<React.SetStateAction<Currency | null>>;
  setRefreshingCurrency: React.Dispatch<React.SetStateAction<boolean>>;
  setFetchingRateFromAPI: React.Dispatch<React.SetStateAction<boolean>>;
  setFetchedRateFromAPI: React.Dispatch<React.SetStateAction<number | null>>;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSnackbarElement: React.Dispatch<React.SetStateAction<React.ReactElement | null>>;
  setEditDecimalPlacesDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingDecimalPlacesCurrency: React.Dispatch<React.SetStateAction<Currency | null>>;
  setDecimalPlaces: React.Dispatch<React.SetStateAction<number>>;
  setUpdatingDecimalPlaces: React.Dispatch<React.SetStateAction<boolean>>;
  
  // UI state values
  newCurrencyCode: { code: string; name: string; symbol: string } | null;
  editingCurrency: Currency | null;
  editCurrencyRate: string;
  currencyToDelete: string;
  currencyToRefresh: Currency | null;
  fetchedRateFromAPI: number | null;
  refreshingCurrency: boolean;
  editingDecimalPlacesCurrency: Currency | null;
  decimalPlaces: number;
  updatingDecimalPlaces: boolean;
}

export interface UseCurrencyHandlersReturn {
  handleAddCurrency: () => Promise<void>;
  handleEditRate: (currency: Currency) => void;
  handleUpdateRate: () => Promise<void>;
  handleEditDecimalPlaces: (currency: Currency) => void;
  handleUpdateDecimalPlaces: () => Promise<void>;
  handleEditDefaultCurrencyDecimalPlaces: () => Promise<void>;
  handleDeleteClick: (currencyCode: string) => void;
  handleDeleteConfirm: () => Promise<void>;
  handleOpenDefaultCurrencyDialog: () => void;
  handleSetDefault: (currencyCode: string) => Promise<void>;
  handleRefreshRateClick: (currency: Currency) => Promise<void>;
  handleRefreshRateConfirm: () => Promise<void>;
  handleCloseRefreshDialog: () => void;
  handleMoveCurrencyUp: (currencyCode: string) => Promise<void>;
  handleMoveCurrencyDown: (currencyCode: string) => Promise<void>;
}

/**
 * Hook for currency operation handlers
 */
export const useCurrencyHandlers = (params: UseCurrencyHandlersParams): UseCurrencyHandlersReturn => {
  const {
    workspaceId,
    defaultCurrency,
    currencies,
    setDefaultCurrency,
    setCurrencies,
    fetchExchangeRatesFromAPI,
    formatRate,
    setAddDialogOpen,
    setNewCurrencyCode,
    setAddingCurrency,
    setEditDialogOpen,
    setEditingCurrency,
    setEditCurrencyRate,
    setDeleteDialogOpen,
    setCurrencyToDelete,
    setDefaultCurrencyDialogOpen,
    setSelectedDefaultCurrency,
    setRefreshDialogOpen,
    setCurrencyToRefresh,
    setRefreshingCurrency,
    setFetchingRateFromAPI,
    setFetchedRateFromAPI,
    setSnackbarOpen,
    setSnackbarElement,
    newCurrencyCode,
    editingCurrency,
    editCurrencyRate,
    currencyToDelete,
    currencyToRefresh,
    fetchedRateFromAPI,
    refreshingCurrency,
    editingDecimalPlacesCurrency,
    decimalPlaces,
    updatingDecimalPlaces,
    setEditDecimalPlacesDialogOpen,
    setEditingDecimalPlacesCurrency,
    setDecimalPlaces,
    setUpdatingDecimalPlaces,
  } = params;

  // Handle add currency
  const handleAddCurrency = async (): Promise<void> => {
    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Currencies cannot be added. Please log in to add currencies." />
      );
      setSnackbarOpen(true);
      setAddDialogOpen(false);
      setNewCurrencyCode(null);
      return;
    }

    if (!workspaceId || !auth.currentUser || !newCurrencyCode) return;

    setAddingCurrency(true);

    try {
      // Fetch rate from API automatically
      const apiRates = await fetchExchangeRatesFromAPI(defaultCurrency, [newCurrencyCode.code]);
      const rate = apiRates[newCurrencyCode.code];

      if (!rate || rate <= 0) {
        alert(`Failed to fetch exchange rate for ${newCurrencyCode.code}. Please try again later.`);
        setAddingCurrency(false);
        return;
      }

      const currencyRef = doc(db, "workspaces", workspaceId, "currencies", newCurrencyCode.code);
      
      // Check if currency already exists
      const existingDoc = await getDoc(currencyRef);
      const existingRates = existingDoc.exists() 
        ? ((existingDoc.data()?.rates as Record<string, number | null>) || {})
        : {};
      
      // Store rate for current default currency
      existingRates[defaultCurrency] = rate;
      
      await setDoc(currencyRef, {
        name: newCurrencyCode.name,
        symbol: newCurrencyCode.symbol,
        rate: rate, // Current rate for default currency (fetched from API)
        rates: existingRates, // All rates per default currency
        manuallyEdited: false, // Not manually edited (fetched from API)
        decimalPlaces: decimalPlaces, // Number of decimal places
        updatedAt: serverTimestamp(),
      }, { merge: true });

      // Add new currency to currency order (at the end)
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      const settingsDoc = await getDoc(settingsRef);
      const currentOrder = settingsDoc.exists() ? (settingsDoc.data().currencyOrder as string[] || []) : [];
      if (!currentOrder.includes(newCurrencyCode.code)) {
        const newOrder = [...currentOrder, newCurrencyCode.code];
        await updateDoc(settingsRef, { currencyOrder: newOrder } as any);
      }

      setAddDialogOpen(false);
      setNewCurrencyCode(null);
      setAddingCurrency(false);
    } catch (error) {
      console.error("Error adding currency:", error);
      alert("Failed to add currency. Please try again later.");
      setAddingCurrency(false);
    }
  };

  // Handle edit currency rate
  const handleEditRate = (currency: Currency): void => {
    // Set the currency being edited
    setEditingCurrency(currency);
    // Convert stored rate (1 defaultCurrency = rate currency) to display format (1 currency = x defaultCurrency)
    // Display format = 1 / stored rate
    if (currency.rate === null) {
      setEditCurrencyRate("");
    } else {
      const displayRate = 1 / currency.rate;
      setEditCurrencyRate(formatRate(displayRate));
    }
    // Open the dialog - this works in both demo mode and normal mode
    setEditDialogOpen(true);
  };

  // Handle update currency rate
  const handleUpdateRate = async (): Promise<void> => {
    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Currency rates cannot be edited. Please log in to edit currency rates." />
      );
      setEditDialogOpen(false);
      setEditingCurrency(null);
      setEditCurrencyRate("1.0");
      setTimeout(() => {
        setSnackbarOpen(true);
      }, 150);
      return;
    }

    if (!workspaceId || !auth.currentUser || !editingCurrency) return;

    if (!editCurrencyRate || editCurrencyRate.trim() === "") {
      alert("Please enter an exchange rate");
      return;
    }

    const displayRate = parseFloat(editCurrencyRate);
    if (isNaN(displayRate) || displayRate <= 0) {
      alert("Please enter a valid exchange rate");
      return;
    }

    try {
      const currencyRef = doc(db, "workspaces", workspaceId, "currencies", editingCurrency.code);
      
      // Get existing rates
      const existingDoc = await getDoc(currencyRef);
      const existingRates = existingDoc.exists()
        ? ((existingDoc.data()?.rates as Record<string, number | null>) || {})
        : {};
      
      // Convert display format (1 currency = x defaultCurrency) to stored format (1 defaultCurrency = rate currency)
      // Stored format = 1 / display rate. Do NOT round here - rounding loses precision (e.g. 377 → 0.00265 → displays as 377.35849)
      const storedRate = 1 / displayRate;
      
      // Update rate for current default currency
      existingRates[defaultCurrency] = storedRate;
      
      await updateDoc(currencyRef, {
        rate: storedRate, // Current rate for default currency (stored format)
        rates: existingRates, // All rates per default currency
        manuallyEdited: true, // Mark as manually edited
        updatedAt: serverTimestamp(),
      });

      setEditDialogOpen(false);
      setEditingCurrency(null);
      setEditCurrencyRate("1.0");
    } catch (error) {
      console.error("Error updating currency rate:", error);
      alert("Failed to update currency rate");
    }
  };

  // Handle edit decimal places (open dialog)
  const handleEditDecimalPlaces = (currency: Currency): void => {
    setEditingDecimalPlacesCurrency(currency);
    setDecimalPlaces(currency.decimalPlaces ?? 2);
    setEditDecimalPlacesDialogOpen(true);
  };

  // Handle update decimal places
  const handleUpdateDecimalPlaces = async (): Promise<void> => {
    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Decimal places cannot be edited. Please log in to edit decimal places." />
      );
      setEditDecimalPlacesDialogOpen(false);
      setEditingDecimalPlacesCurrency(null);
      setTimeout(() => {
        setSnackbarOpen(true);
      }, 150);
      return;
    }

    if (!workspaceId || (!auth.currentUser && !isBypassAuthEnabled()) || !editingDecimalPlacesCurrency) return;

    // Validate decimal places
    if (decimalPlaces < 0 || decimalPlaces > 4 || !Number.isInteger(decimalPlaces)) {
      alert("Decimal places must be between 0 and 4");
      return;
    }

    setUpdatingDecimalPlaces(true);

    try {
      const currencyRef = doc(db, "workspaces", workspaceId, "currencies", editingDecimalPlacesCurrency.code);
      
      // Check if currency exists
      const currencyDoc = await getDoc(currencyRef);
      
      if (!currencyDoc.exists()) {
        // Currency doesn't exist (e.g., default currency that was never explicitly added)
        // Create it with the decimal places
        const currencyInfo = COMMON_CURRENCIES.find((c) => c.code === editingDecimalPlacesCurrency.code);
        if (!currencyInfo) {
          alert("Currency not found in available currencies");
          setUpdatingDecimalPlaces(false);
          return;
        }

        await setDoc(currencyRef, {
          name: currencyInfo.name,
          symbol: currencyInfo.symbol,
          rate: editingDecimalPlacesCurrency.code === defaultCurrency ? 1.0 : null,
          rates: editingDecimalPlacesCurrency.code === defaultCurrency ? { [defaultCurrency]: 1.0 } : {},
          manuallyEdited: false,
          decimalPlaces: decimalPlaces,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } else {
        // Currency exists, update decimal places
        await updateDoc(currencyRef, {
          decimalPlaces: decimalPlaces,
          updatedAt: serverTimestamp(),
        });
      }

      // Update local state (only if not default currency, since default is filtered out)
      if (editingDecimalPlacesCurrency.code !== defaultCurrency) {
        setCurrencies((prevCurrencies) =>
          prevCurrencies.map((c) =>
            c.code === editingDecimalPlacesCurrency.code ? { ...c, decimalPlaces } : c
          )
        );
      }

      setEditDecimalPlacesDialogOpen(false);
      setEditingDecimalPlacesCurrency(null);
      setUpdatingDecimalPlaces(false);
    } catch (error) {
      console.error("Error updating decimal places:", error);
      alert("Failed to update decimal places");
      setUpdatingDecimalPlaces(false);
    }
  };

  // Handle edit default currency decimal places
  const handleEditDefaultCurrencyDecimalPlaces = async (): Promise<void> => {
    if (!workspaceId || !defaultCurrency) return;

    try {
      // Get default currency info from COMMON_CURRENCIES
      const currencyInfo = COMMON_CURRENCIES.find((c) => c.code === defaultCurrency);
      if (!currencyInfo) {
        alert("Default currency not found in available currencies");
        return;
      }

      // Check if default currency exists in currencies collection
      const currencyRef = doc(db, "workspaces", workspaceId, "currencies", defaultCurrency);
      const currencyDoc = await getDoc(currencyRef);

      let defaultCurrencyData: Currency;

      if (currencyDoc.exists()) {
        // Currency exists, get its data
        const data = currencyDoc.data();
        const rates = (data.rates as Record<string, number | null>) || {};
        let currentRate: number | null;
        if (rates[defaultCurrency] !== undefined) {
          currentRate = rates[defaultCurrency];
        } else if (typeof data.rate === "number") {
          currentRate = data.rate;
        } else {
          currentRate = 1.0; // Default currency always has rate 1.0 to itself
        }

        defaultCurrencyData = {
          code: defaultCurrency,
          name: (data.name as string) || currencyInfo.name,
          symbol: (data.symbol as string) || currencyInfo.symbol,
          rate: currentRate,
          rates: rates,
          manuallyEdited: data.manuallyEdited === true,
          decimalPlaces: data.decimalPlaces !== undefined ? (data.decimalPlaces as number) : getDefaultDecimalPlaces(defaultCurrency),
          updatedAt: data.updatedAt || data.createdAt || Timestamp.now(),
        };
      } else {
        // Currency doesn't exist, create a default Currency object
        defaultCurrencyData = {
          code: defaultCurrency,
          name: currencyInfo.name,
          symbol: currencyInfo.symbol,
          rate: 1.0, // Default currency always has rate 1.0 to itself
          rates: { [defaultCurrency]: 1.0 },
          manuallyEdited: false,
          decimalPlaces: getDefaultDecimalPlaces(defaultCurrency),
          updatedAt: Timestamp.now(),
        };
      }

      // Open edit dialog with default currency
      setEditingDecimalPlacesCurrency(defaultCurrencyData);
      setDecimalPlaces(defaultCurrencyData.decimalPlaces ?? 2);
      setEditDecimalPlacesDialogOpen(true);
    } catch (error) {
      console.error("Error loading default currency:", error);
      alert("Failed to load default currency information");
    }
  };

  // Handle delete currency
  const handleDeleteClick = (currencyCode: string): void => {
    if (currencyCode === defaultCurrency) {
      alert("Cannot delete the default currency. Please set another currency as default first.");
      return;
    }
    setCurrencyToDelete(currencyCode);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Currencies cannot be deleted. Please log in to delete currencies." />
      );
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      setCurrencyToDelete("");
      return;
    }

    if (!workspaceId || !auth.currentUser || !currencyToDelete) return;

    try {
      await deleteDoc(doc(db, "workspaces", workspaceId, "currencies", currencyToDelete));

      // Remove currency from currency order
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      const settingsDoc = await getDoc(settingsRef);
      if (settingsDoc.exists()) {
        const currentOrder = (settingsDoc.data().currencyOrder as string[] || []).filter(
          (code) => code !== currencyToDelete
        );
        await updateDoc(settingsRef, { currencyOrder: currentOrder } as any);
      }

      setDeleteDialogOpen(false);
      setCurrencyToDelete("");
    } catch (error) {
      console.error("Error deleting currency:", error);
      alert("Failed to delete currency");
    }
  };

  // Handle open default currency dialog
  const handleOpenDefaultCurrencyDialog = (): void => {
    setSelectedDefaultCurrency(defaultCurrency);
    setDefaultCurrencyDialogOpen(true);
  };

  // Handle refresh rate from API
  const handleRefreshRateClick = async (currency: Currency): Promise<void> => {
    setCurrencyToRefresh(currency);
    setRefreshDialogOpen(true);
    setFetchedRateFromAPI(null);
    setFetchingRateFromAPI(true);

    // In demo mode, still fetch from API to show the full flow, but the save will be blocked
    if (isDemoMode() && !isBypassAuthEnabled()) {
      // For demo mode, we can still fetch to show the flow, but skip the actual API call
      // to avoid unnecessary requests. Set a placeholder based on current rate.
      setTimeout(() => {
        setFetchedRateFromAPI(currency.rate || null);
        setFetchingRateFromAPI(false);
      }, 500); // Simulate API delay
      return;
    }

    try {
      // Fetch rate from API to show in dialog
      const apiRates = await fetchExchangeRatesFromAPI(defaultCurrency, [currency.code]);
      const newRate = apiRates[currency.code];
      setFetchedRateFromAPI(newRate && newRate > 0 ? newRate : null);
    } catch (error) {
      console.error("Error fetching rate from API:", error);
      setFetchedRateFromAPI(null);
    } finally {
      setFetchingRateFromAPI(false);
    }
  };

  // Handle confirm refresh rate from API
  const handleRefreshRateConfirm = async (): Promise<void> => {
    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Currency rates cannot be refreshed. Please log in to refresh currency rates." />
      );
      setRefreshDialogOpen(false);
      setCurrencyToRefresh(null);
      setFetchedRateFromAPI(null);
      setFetchingRateFromAPI(false);
      setTimeout(() => {
        setSnackbarOpen(true);
      }, 150);
      return;
    }

    if (!workspaceId || !auth.currentUser || !currencyToRefresh || !fetchedRateFromAPI) return;

    setRefreshingCurrency(true);

    try {
      const currencyRef = doc(db, "workspaces", workspaceId, "currencies", currencyToRefresh.code);
      
      // Get existing rates
      const existingDoc = await getDoc(currencyRef);
      const existingRates = existingDoc.exists()
        ? ((existingDoc.data()?.rates as Record<string, number | null>) || {})
        : {};
      
      // Update rate for current default currency
      existingRates[defaultCurrency] = fetchedRateFromAPI;
      
      await updateDoc(currencyRef, {
        rate: fetchedRateFromAPI, // Current rate for default currency (from API)
        rates: existingRates, // All rates per default currency
        manuallyEdited: false, // Reset to false since rate is now from API
        updatedAt: serverTimestamp(),
      });

      setRefreshingCurrency(false);
      setRefreshDialogOpen(false);
      setCurrencyToRefresh(null);
      setFetchedRateFromAPI(null);
      setFetchingRateFromAPI(false);
    } catch (error) {
      console.error("Error refreshing currency rate:", error);
      alert("Failed to refresh currency rate from API");
      setRefreshingCurrency(false);
    }
  };

  // Handle close refresh dialog
  const handleCloseRefreshDialog = (): void => {
    if (!refreshingCurrency) {
      setRefreshDialogOpen(false);
      setCurrencyToRefresh(null);
      setFetchedRateFromAPI(null);
      setFetchingRateFromAPI(false);
    }
  };

  // Handle set default currency
  const handleSetDefault = async (currencyCode: string): Promise<void> => {
    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Default currency cannot be changed. Please log in to change the default currency." />
      );
      setSnackbarOpen(true);
      setDefaultCurrencyDialogOpen(false);
      return;
    }

    if (!workspaceId || !auth.currentUser || currencyCode === defaultCurrency) return;

    try {
      const oldDefaultCurrency = defaultCurrency;
      const batch = writeBatch(db);

      // 1. Fetch all currencies from Firestore (including default)
      const currenciesRef = collection(db, "workspaces", workspaceId, "currencies");
      const allCurrenciesSnapshot = await getDocs(currenciesRef);
      
      // 2. Always fetch rates from API for currencies that don't have a saved rate
      const currencyCodes = allCurrenciesSnapshot.docs.map((docSnap) => docSnap.id);
      const apiRates = await fetchExchangeRatesFromAPI(currencyCode, currencyCodes);
      
      // 3. Save current rates for old default currency and restore rates for new default
      for (const docSnap of allCurrenciesSnapshot.docs) {
        const currencyCodeInDoc = docSnap.id;
        const data = docSnap.data();
        const currencyRef = doc(db, "workspaces", workspaceId, "currencies", currencyCodeInDoc);
        
        const currentRates = (data.rates as Record<string, number | null>) || {};
        
        // Save current rate for old default currency
        if (currencyCodeInDoc !== oldDefaultCurrency) {
          // Get current rate (from rate field or rates[oldDefaultCurrency])
          const currentRate = data.rate !== null && data.rate !== undefined 
            ? (data.rate as number) 
            : (currentRates[oldDefaultCurrency] ?? null);
          currentRates[oldDefaultCurrency] = currentRate;
        }
        
        // Get rate for new default currency (restore if exists and not null, otherwise use API)
        let newRate: number | null;
        if (currencyCodeInDoc === currencyCode) {
          // New default currency always has rate 1.0 to itself
          newRate = 1.0;
          currentRates[currencyCode] = 1.0;
        } else if (currentRates[currencyCode] !== undefined && currentRates[currencyCode] !== null) {
          // Restore previously saved rate (non-null)
          newRate = currentRates[currencyCode];
        } else if (apiRates[currencyCodeInDoc] !== undefined && apiRates[currencyCodeInDoc] !== null) {
          // No saved rate (or null), but we have API rate - use API rate
          newRate = apiRates[currencyCodeInDoc];
          currentRates[currencyCode] = newRate;
        } else {
          // No saved rate and no API rate - set to null
          newRate = null;
          currentRates[currencyCode] = null;
        }
        
        batch.update(currencyRef, {
          rates: currentRates,
          rate: newRate, // Update current rate
          updatedAt: serverTimestamp(),
        });
      }

      // 5. Create new default currency document if it doesn't exist
      const newDefaultCurrencyRef = doc(db, "workspaces", workspaceId, "currencies", currencyCode);
      const newDefaultCurrencyDoc = await getDoc(newDefaultCurrencyRef);
      
      if (!newDefaultCurrencyDoc.exists()) {
        const currencyInfo = COMMON_CURRENCIES.find((c) => c.code === currencyCode);
        if (currencyInfo) {
          const decimalPlaces = getDefaultDecimalPlaces(currencyCode);
          batch.set(newDefaultCurrencyRef, {
            name: currencyInfo.name,
            symbol: currencyInfo.symbol,
            rate: 1.0, // Default currency always has rate 1.0 to itself
            rates: { [currencyCode]: 1.0 }, // Initialize rates
            manuallyEdited: false, // Not manually edited (created by system)
            decimalPlaces: decimalPlaces,
            updatedAt: serverTimestamp(),
          });
        }
      } else {
        // Update existing default currency document
        const existingData = newDefaultCurrencyDoc.data();
        const existingRates = (existingData?.rates as Record<string, number>) || {};
        existingRates[currencyCode] = 1.0; // Default currency always has rate 1.0 to itself
        
        batch.update(newDefaultCurrencyRef, {
          rate: 1.0,
          rates: existingRates,
          updatedAt: serverTimestamp(),
        });
      }

      // 6. Update settings
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      batch.set(settingsRef, { defaultCurrency: currencyCode }, { merge: true });

      await batch.commit();
      
      setDefaultCurrency(currencyCode);
      setDefaultCurrencyDialogOpen(false);
    } catch (error) {
      console.error("Error setting default currency:", error);
      alert("Failed to set default currency");
    }
  };

  const handleMoveCurrencyUp = async (currencyCode: string): Promise<void> => {
    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Currency order cannot be changed. Please log in to reorder currencies." />
      );
      setSnackbarOpen(true);
      return;
    }

    if (!workspaceId || !auth.currentUser) return;

    try {
      // Get current currency order from settings
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      const settingsDoc = await getDoc(settingsRef);
      const currentOrder = settingsDoc.exists() ? (settingsDoc.data().currencyOrder as string[] || []) : [];

      // Find current index
      const currentIndex = currentOrder.indexOf(currencyCode);
      if (currentIndex <= 0) return; // Already at top or not in order

      // Swap with previous currency
      const newOrder = [...currentOrder];
      [newOrder[currentIndex - 1], newOrder[currentIndex]] = [newOrder[currentIndex], newOrder[currentIndex - 1]];

      // Save new order
      await updateDoc(settingsRef, { currencyOrder: newOrder } as any);

      // Update local state immediately for better UX
      const reorderedCurrencies = [...currencies];
      const currencyIndex = reorderedCurrencies.findIndex((c) => c.code === currencyCode);
      const prevIndex = reorderedCurrencies.findIndex((c) => c.code === newOrder[currentIndex - 1]);
      if (currencyIndex !== -1 && prevIndex !== -1) {
        [reorderedCurrencies[prevIndex], reorderedCurrencies[currencyIndex]] = [
          reorderedCurrencies[currencyIndex],
          reorderedCurrencies[prevIndex],
        ];
        setCurrencies(reorderedCurrencies);
      }
    } catch (error) {
      console.error("Error moving currency up:", error);
      alert("Failed to reorder currency");
    }
  };

  const handleMoveCurrencyDown = async (currencyCode: string): Promise<void> => {
    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Currency order cannot be changed. Please log in to reorder currencies." />
      );
      setSnackbarOpen(true);
      return;
    }

    if (!workspaceId || !auth.currentUser) return;

    try {
      // Get current currency order from settings
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      const settingsDoc = await getDoc(settingsRef);
      const currentOrder = settingsDoc.exists() ? (settingsDoc.data().currencyOrder as string[] || []) : [];

      // Find current index
      const currentIndex = currentOrder.indexOf(currencyCode);
      if (currentIndex === -1 || currentIndex >= currentOrder.length - 1) return; // Already at bottom or not in order

      // Swap with next currency
      const newOrder = [...currentOrder];
      [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];

      // Save new order
      await updateDoc(settingsRef, { currencyOrder: newOrder } as any);

      // Update local state immediately for better UX
      const reorderedCurrencies = [...currencies];
      const currencyIndex = reorderedCurrencies.findIndex((c) => c.code === currencyCode);
      const nextIndex = reorderedCurrencies.findIndex((c) => c.code === newOrder[currentIndex + 1]);
      if (currencyIndex !== -1 && nextIndex !== -1) {
        [reorderedCurrencies[currencyIndex], reorderedCurrencies[nextIndex]] = [
          reorderedCurrencies[nextIndex],
          reorderedCurrencies[currencyIndex],
        ];
        setCurrencies(reorderedCurrencies);
      }
    } catch (error) {
      console.error("Error moving currency down:", error);
      alert("Failed to reorder currency");
    }
  };

  return {
    handleAddCurrency,
    handleEditRate,
    handleUpdateRate,
    handleEditDecimalPlaces,
    handleUpdateDecimalPlaces,
    handleEditDefaultCurrencyDecimalPlaces,
    handleDeleteClick,
    handleDeleteConfirm,
    handleOpenDefaultCurrencyDialog,
    handleSetDefault,
    handleRefreshRateClick,
    handleRefreshRateConfirm,
    handleCloseRefreshDialog,
    handleMoveCurrencyUp,
    handleMoveCurrencyDown,
  };
};
